import React, { useState, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { 
  FaShippingFast, 
  FaCheckCircle, 
  FaBox, 
  FaHome, 
  FaClock, 
  FaTruck, 
  FaSpinner,
  FaExclamationTriangle,
  FaUndoAlt,
  FaMoneyBillWave,
  FaTimesCircle,
  FaHandPaper,
  FaCalendarAlt,
  FaUser,
  FaMapMarkerAlt,
  FaPhone
} from "react-icons/fa";
import { GetOrderStatus, GetOrderTrackingByProductAndOrder } from "../../Data.js";
import config from "../../config";

const OrderTracking = ({ 
  orderId: propOrderId = null, 
  productId: propProductId = null,
  showCarrier = true, 
  showExpectedDelivery = true 
}) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [trackingData, setTrackingData] = useState(null);
  const [orderStatuses, setOrderStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get order and product IDs from multiple sources
  const orderID = propOrderId || 
                  searchParams.get('orderId') || 
                  location.state?.orderID ||
                  location.state?.orderId;
                  
  const productID = propProductId || 
                   searchParams.get('productId') || 
                   location.state?.productID;

  // Fetch order statuses from API
  const fetchOrderStatuses = async () => {
    try {
      const statuses = await GetOrderStatus();
      setOrderStatuses(statuses);
      return statuses;
    } catch (error) {
      console.error("Error fetching order statuses:", error);
      return [];
    }
  };

  // Fetch tracking data from API
  const fetchTrackingData = async (orderID, productID) => {
    try {
      const data = await GetOrderTrackingByProductAndOrder(orderID,productID);
      return Array.isArray(data) && data.length > 0 ? data[0] : data; // Get first tracking record
    } catch (error) {
      console.error("Error fetching tracking data:", error);
      return null;
    }
  };

  // Map status names to icons and colors
  const getStatusIcon = (statusName) => {
    const normalizedStatus = statusName?.toLowerCase() || '';
    
    if (normalizedStatus.includes('processing')) return FaBox;
    if (normalizedStatus.includes('pending')) return FaClock;
    if (normalizedStatus.includes('confirmed')) return FaCheckCircle;
    if (normalizedStatus.includes('shipped')) return FaShippingFast;
    if (normalizedStatus.includes('pickup')) return FaHandPaper;
    if (normalizedStatus.includes('delivered')) return FaHome;
    if (normalizedStatus.includes('cancelled')) return FaTimesCircle;
    if (normalizedStatus.includes('returned')) return FaUndoAlt;
    if (normalizedStatus.includes('refunded')) return FaMoneyBillWave;
    
    return FaBox;
  };

  const getStatusColor = (statusName) => {
    const normalizedStatus = statusName?.toLowerCase() || '';
    
    if (normalizedStatus.includes('processing')) return 'indigo';
    if (normalizedStatus.includes('pending')) return 'amber';
    if (normalizedStatus.includes('confirmed')) return 'emerald';
    if (normalizedStatus.includes('shipped')) return 'cyan';
    if (normalizedStatus.includes('pickup')) return 'purple';
    if (normalizedStatus.includes('delivered')) return 'green';
    if (normalizedStatus.includes('cancelled')) return 'red';
    if (normalizedStatus.includes('returned')) return 'orange';
    if (normalizedStatus.includes('refunded')) return 'rose';
    
    return 'gray';
  };

  // Helper functions for consistent color theming
  const getStatusBgColor = (statusName) => {
    const color = getStatusColor(statusName);
    const colorMap = {
      'indigo': 'bg-indigo-500',
      'amber': 'bg-amber-500',
      'emerald': 'bg-emerald-500',
      'cyan': 'bg-cyan-500',
      'purple': 'bg-purple-500',
      'green': 'bg-green-500',
      'red': 'bg-red-500',
      'orange': 'bg-orange-500',
      'rose': 'bg-rose-500',
      'gray': 'bg-gray-500'
    };
    return colorMap[color] || 'bg-gray-500';
  };

  const getStatusRingColor = (statusName) => {
    const color = getStatusColor(statusName);
    const colorMap = {
      'indigo': 'ring-indigo-200',
      'amber': 'ring-amber-200',
      'emerald': 'ring-emerald-200',
      'cyan': 'ring-cyan-200',
      'purple': 'ring-purple-200',
      'green': 'ring-green-200',
      'red': 'ring-red-200',
      'orange': 'ring-orange-200',
      'rose': 'ring-rose-200',
      'gray': 'ring-gray-200'
    };
    return colorMap[color] || 'ring-gray-200';
  };

  const getStatusTextColor = (statusName) => {
    const color = getStatusColor(statusName);
    const colorMap = {
      'indigo': 'text-indigo-700',
      'amber': 'text-amber-700',
      'emerald': 'text-emerald-700',
      'cyan': 'text-cyan-700',
      'purple': 'text-purple-700',
      'green': 'text-green-700',
      'red': 'text-red-700',
      'orange': 'text-orange-700',
      'rose': 'text-rose-700',
      'gray': 'text-gray-700'
    };
    return colorMap[color] || 'text-gray-700';
  };

  // Generate status timeline based on current status
  const generateStatusTimeline = (currentStatus, previousStatus, statuses, trackingInfo) => {
    const currentStatusObj = statuses.find(s => s.status === currentStatus);
    const previousStatusObj = statuses.find(s => s.status === previousStatus);
    
    if (!currentStatusObj) return [];

    // Define the typical order flow
    const normalFlow = ['Processing', 'Pending Confirmation', 'Confirmed', 'Shipped', 'Delivered'];
    const specialStatuses = ['Cancelled', 'Returned', 'Refunded'];
    
    // Handle special cases (cancelled, returned, refunded)
    if (specialStatuses.some(status => currentStatus.includes(status))) {
      const steps = [
        {
          id: 1,
          status: 'Processing',
          completed: true,
          timestamp: trackingInfo?.createdOn
        },
        {
          id: currentStatusObj.statusID,
          status: currentStatus,
          completed: true,
          timestamp: trackingInfo?.trackingDate,
          isSpecial: true
        }
      ];
      
      // Add previous status if it exists and is different
      if (previousStatusObj && previousStatus !== currentStatus && !previousStatus.includes('Processing')) {
        steps.splice(1, 0, {
          id: previousStatusObj.statusID,
          status: previousStatus,
          completed: true,
          timestamp: trackingInfo?.createdOn
        });
      }
      
      return steps;
    }

    // Normal flow - show all steps up to current status
    const timeline = [];
    const currentStatusIndex = normalFlow.findIndex(flowStatus => 
      currentStatus.toLowerCase().includes(flowStatus.toLowerCase())
    );
    
    for (let i = 0; i < normalFlow.length; i++) {
      const flowStatus = normalFlow[i];
      const statusObj = statuses.find(s => s.status === flowStatus);
      
      if (statusObj) {
        const isCompleted = currentStatusIndex >= i;
        const isCurrent = currentStatusIndex === i;
        
        timeline.push({
          id: statusObj.statusID,
          status: flowStatus,
          completed: isCompleted,
          timestamp: isCompleted ? trackingInfo?.trackingDate : null,
          isCurrent: isCurrent
        });
      }
    }
    
    return timeline;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!orderID) {
        setError("No order ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch both order statuses and tracking data
        const [statuses, tracking] = await Promise.all([
          fetchOrderStatuses(),
          productID ? fetchTrackingData(orderID, productID) : null
        ]);
        
        setOrderStatuses(statuses);
        setTrackingData(tracking);
        
        if (!tracking && productID) {
          setError("No tracking information found for this order and product");
        } else if (!productID) {
          setError("Product ID is required for tracking");
        }
        
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load tracking information");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orderID, productID]);

  const timeline = trackingData && orderStatuses.length > 0 ? 
    generateStatusTimeline(trackingData.currentStatus, trackingData.previousStatus, orderStatuses, trackingData) : [];
  
  const currentStatusObj = orderStatuses.find(s => s.status === trackingData?.currentStatus);
  const StatusIcon = currentStatusObj ? getStatusIcon(currentStatusObj.status) : FaBox;
  const statusColor = currentStatusObj ? getStatusColor(currentStatusObj.status) : 'gray';
  const isSpecialCase = timeline.some(step => step.isSpecial);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-yellow-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl text-center max-w-md w-full border border-blue-200">
          <FaSpinner className="animate-spin text-5xl text-blue-600 mx-auto mb-6" />
          <h3 className="text-xl font-bold text-gray-800 mb-3">Loading Order Tracking</h3>
          <p className="text-gray-600">Fetching your order details...</p>
          <div className="mt-6 flex justify-center">
            <div className="animate-pulse flex space-x-2">
              <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
              <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
              <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !trackingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-yellow-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl text-center max-w-md w-full border border-red-200">
          <FaExclamationTriangle className="text-6xl text-red-500 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Unable to Load Tracking</h2>
          <p className="text-gray-600 mb-6">{error || "Tracking information not found"}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-3 rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 transform hover:scale-105 font-medium shadow-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-yellow-50">
      <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
        
        {/* Enhanced Header Section */}
        <div className="bg-white shadow-2xl rounded-2xl border border-blue-200 mb-8 overflow-hidden">
          {/* Gradient Header */}
          <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-yellow-500 p-6 text-white relative">
            <div className="absolute inset-0 opacity-20">
              <div className="w-full h-full" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
              }}></div>
            </div>
            
            <div className="relative">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="mb-4 lg:mb-0">
                  <h1 className="text-3xl font-bold mb-3 flex items-center">
                    <FaTruck className="mr-3 text-white" />
                    Order Tracking
                  </h1>
                  <div className="space-y-2">
                    <p className="text-blue-100 flex items-center">
                      <span className="font-medium mr-2">Order ID:</span>
                      <span className="font-mono bg-white/20 px-3 py-1 rounded-lg text-white font-bold">
                        {orderID}
                      </span>
                    </p>
                    <p className="text-blue-100 flex items-center">
                      <span className="font-medium mr-2">Tracking ID:</span>
                      <span className="font-mono bg-white/20 px-3 py-1 rounded-lg text-white font-bold">
                        {trackingData.trackingID}
                      </span>
                    </p>
                  </div>
                </div>
                
                {/* Current Status Badge */}
                <div className="flex flex-col items-start lg:items-end">
                  <div className={`inline-flex items-center px-4 sm:px-6 py-3 rounded-xl text-xs sm:text-sm font-bold mb-3 backdrop-blur-sm border-2 transition-all duration-200 ${
                    statusColor === 'amber' ? 'bg-amber-100 text-amber-800 border-amber-300' :
                    statusColor === 'indigo' ? 'bg-indigo-100 text-indigo-800 border-indigo-300' :
                    statusColor === 'emerald' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' :
                    statusColor === 'cyan' ? 'bg-cyan-100 text-cyan-800 border-cyan-300' :
                    statusColor === 'purple' ? 'bg-purple-100 text-purple-800 border-purple-300' :
                    statusColor === 'green' ? 'bg-green-100 text-green-800 border-green-300' :
                    statusColor === 'red' ? 'bg-red-100 text-red-800 border-red-300' :
                    statusColor === 'orange' ? 'bg-orange-100 text-orange-800 border-orange-300' :
                    statusColor === 'rose' ? 'bg-rose-100 text-rose-800 border-rose-300' :
                    'bg-gray-100 text-gray-800 border-gray-300'
                  }`}>
                    <StatusIcon className="mr-2 text-lg" />
                    {trackingData.currentStatus}
                  </div>
                  
                  {/* Carrier Info */}
                  {showCarrier && trackingData.carrier && (
                    <div className="text-blue-100 flex items-center mb-2">
                      <FaTruck className="mr-2" />
                      <span className="font-medium">{trackingData.carrier}</span>
                    </div>
                  )}
                  
                  {/* Expected Delivery */}
                  {showExpectedDelivery && trackingData.expectedDeliveryDate && !isSpecialCase && (
                    <div className="text-blue-100 flex items-center">
                      <FaCalendarAlt className="mr-2" />
                      <span className="font-medium">
                        Expected: {new Date(trackingData.expectedDeliveryDate).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Status Description */}
          {currentStatusObj && (
            <div className="p-6 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
              <div className="flex items-start">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 ${
                  statusColor === 'yellow' ? 'bg-yellow-100 text-yellow-600' :
                  statusColor === 'blue' ? 'bg-blue-100 text-blue-600' :
                  statusColor === 'red' ? 'bg-red-100 text-red-600' :
                  statusColor === 'orange' ? 'bg-orange-100 text-orange-600' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  <StatusIcon className="text-xl" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-800 mb-1">
                    {currentStatusObj.status}
                  </h3>
                  <p className="text-gray-600 text-sm mb-2">
                    {currentStatusObj.description}
                  </p>
                  {trackingData.trackingDate && (
                    <p className="text-xs text-gray-500 flex items-center">
                      <FaClock className="mr-1" />
                      Updated on {new Date(trackingData.trackingDate).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Enhanced Progress Timeline */}
        <div className="bg-white shadow-2xl rounded-2xl border border-gray-200 p-6 sm:p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-8 flex items-center">
            <FaMapMarkerAlt className="mr-3 text-blue-600" />
            Order Progress Timeline
          </h2>

          {timeline.length > 0 ? (
            <>
              {/* Mobile View - Vertical Timeline */}
              <div className="block lg:hidden space-y-6">
                {timeline.map((step, index) => {
                  const statusObj = orderStatuses.find(s => s.status === step.status);
                  const StepIcon = getStatusIcon(step.status);
                  const stepColor = getStatusColor(step.status);
                  
                  return (
                    <div key={step.id} className="flex items-start gap-4">
                      {/* Timeline Line */}
                      {index < timeline.length - 1 && (
                        <div className="absolute left-6 mt-12 w-0.5 h-16 bg-gray-300 z-0"></div>
                      )}
                      
                      {/* Step Icon */}
                      <div className={`relative z-10 w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${
                        step.completed 
                          ? step.isSpecial
                            ? 'bg-red-500 text-white ring-4 ring-red-200'
                            : step.isCurrent
                              ? getStatusBgColor(step.status) + ' text-white ring-4 ' + getStatusRingColor(step.status)
                              : getStatusBgColor(step.status) + ' text-white ring-4 ' + getStatusRingColor(step.status)
                          : 'bg-gray-200 text-gray-400'
                      }`}>
                        <StepIcon className="text-sm sm:text-lg" />
                      </div>
                      
                      {/* Step Details */}
                      <div className="flex-1 pb-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className={`font-bold text-base sm:text-lg ${
                            step.completed ? 
                              step.isSpecial
                                ? 'text-red-700'
                                : getStatusTextColor(step.status)
                              : 'text-gray-500'
                          }`}>
                            {step.status}
                          </h4>
                          {step.completed && (
                            <FaCheckCircle className={`text-lg ${
                              step.isSpecial 
                                ? 'text-red-500'
                                : 'text-green-500'
                            }`} />
                          )}
                        </div>
                        
                        {statusObj && (
                          <p className="text-sm text-gray-600 mb-3">{statusObj.description}</p>
                        )}
                        
                        {step.timestamp && (
                          <div className="flex items-center text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
                            <FaClock className="mr-2" />
                            {new Date(step.timestamp).toLocaleString('en-US', { 
                              month: 'short', 
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop View - Horizontal Timeline */}
              <div className="hidden lg:block">
                {/* Progress Line */}
                <div className="relative flex items-center justify-between mb-12">
                  <div className="absolute top-6 left-0 right-0 h-3 bg-gray-300 rounded-full z-0">
                    <div 
                      className={`h-full rounded-full transition-all duration-1000 ${
                        isSpecialCase 
                          ? 'bg-red-500'
                          : 'bg-gradient-to-r from-indigo-500 via-purple-500 to-emerald-500'
                      }`}
                      style={{ 
                        width: `${timeline.length > 1 ? ((timeline.filter(s => s.completed).length - 1) / (timeline.length - 1)) * 100 : 100}%` 
                      }}
                    />
                  </div>

                  {timeline.map((step, index) => {
                    const statusObj = orderStatuses.find(s => s.status === step.status);
                    const StepIcon = getStatusIcon(step.status);
                    
                    return (
                      <div key={step.id} className="relative flex flex-col items-center z-10 flex-1">
                        {/* Step Icon */}
                        <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center shadow-xl transition-all duration-300 transform ${
                          step.completed 
                            ? step.isSpecial
                              ? 'bg-red-500 text-white scale-110 ring-4 ring-red-200'
                              : step.isCurrent
                                ? getStatusBgColor(step.status) + ' text-white scale-110 ring-4 ' + getStatusRingColor(step.status)
                                : getStatusBgColor(step.status) + ' text-white scale-110 ring-4 ' + getStatusRingColor(step.status)
                            : 'bg-gray-200 text-gray-400 hover:bg-gray-300'
                        }`}>
                          <StepIcon className="text-lg sm:text-xl" />
                        </div>

                        {/* Step Label & Details */}
                        <div className="flex flex-col items-center mt-6 text-center max-w-xs">
                          <span className={`text-xs sm:text-sm font-bold mb-2 ${
                            step.completed 
                              ? step.isSpecial
                                ? 'text-red-700'
                                : getStatusTextColor(step.status)
                              : 'text-gray-500'
                          }`}>
                            {step.status}
                          </span>
                          
                          {statusObj && (
                            <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                              {statusObj.description}
                            </p>
                          )}
                          
                          {step.timestamp && (
                            <div className="flex items-center text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                              <FaClock className="mr-1" />
                              {new Date(step.timestamp).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric'
                              })}
                            </div>
                          )}
                          
                          {step.completed && (
                            <div className="mt-2">
                              <FaCheckCircle className={`text-lg ${
                                step.isSpecial 
                                  ? 'text-red-500'
                                  : 'text-green-500'
                              }`} />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <FaClock className="text-6xl text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No Timeline Available</h3>
              <p className="text-gray-500">Tracking information is being processed</p>
            </div>
          )}
        </div>

        {/* Enhanced Information Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Tracking Details */}
          <div className="bg-white shadow-2xl rounded-2xl border border-gray-200 p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
              <FaTruck className="mr-3 text-blue-600" />
              Tracking Details
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-gray-600 font-medium">Merchant ID:</span>
                <span className="font-mono text-sm bg-gray-100 px-3 py-1 rounded">
                  {trackingData.merchantID}
                </span>
              </div>
              
              {showCarrier && trackingData.carrier && (
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">Carrier:</span>
                  <span className="font-semibold text-gray-800">{trackingData.carrier}</span>
                </div>
              )}
              
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-gray-600 font-medium">Previous Status:</span>
                <span className="text-sm text-gray-700">{trackingData.previousStatus}</span>
              </div>
              
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-gray-600 font-medium">Created By:</span>
                <div className="flex items-center">
                  <FaUser className="mr-2 text-gray-400" />
                  <span className="text-sm text-gray-700">{trackingData.createdBy}</span>
                </div>
              </div>
              
              {trackingData.updatedBy && trackingData.updatedBy !== trackingData.createdBy && (
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">Updated By:</span>
                  <div className="flex items-center">
                    <FaUser className="mr-2 text-gray-400" />
                    <span className="text-sm text-gray-700">{trackingData.updatedBy}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Delivery Information */}
          <div className="bg-white shadow-2xl rounded-2xl border border-gray-200 p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
              <FaHome className="mr-3 text-green-600" />
              Delivery Information
            </h3>
            
            <div className="space-y-4">
              {showExpectedDelivery && trackingData.expectedDeliveryDate && (
                <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-xl border border-green-200">
                  <div className="flex items-center justify-between">
                    <span className="text-green-700 font-medium flex items-center">
                      <FaCalendarAlt className="mr-2" />
                      Expected Delivery:
                    </span>
                    <span className="font-bold text-green-800">
                      {new Date(trackingData.expectedDeliveryDate).toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-gray-600 font-medium">Current Status:</span>
                <span className={`font-bold ${
                  isSpecialCase ? 'text-red-600' : 'text-green-600'
                }`}>
                  {trackingData.currentStatus}
                </span>
              </div>
              
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-gray-600 font-medium">Tracking Date:</span>
                <span className="text-sm text-gray-700">
                  {new Date(trackingData.trackingDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
              
              {trackingData.updatedOn && (
                <div className="flex items-center justify-between py-3">
                  <span className="text-gray-600 font-medium">Last Updated:</span>
                  <span className="text-sm text-gray-700">
                    {new Date(trackingData.updatedOn).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Next Steps Information */}
        {!isSpecialCase && timeline.length > 0 && (
          <div className="bg-gradient-to-r from-blue-50 to-yellow-50 rounded-2xl p-6 border border-blue-200 mb-8">
            <div className="flex items-start">
              <FaClock className="text-blue-600 mt-1 mr-4 text-xl" />
              <div className="flex-1">
                <h4 className="text-lg font-bold text-blue-800 mb-2">What's Next?</h4>
                {(() => {
                  const nextStep = timeline.find(s => !s.completed);
                  if (nextStep) {
                    const nextStatusObj = orderStatuses.find(s => s.status === nextStep.status);
                    return (
                      <div>
                        <p className="text-blue-700 font-medium mb-1">
                          Next: {nextStep.status}
                        </p>
                        {nextStatusObj && (
                          <p className="text-sm text-blue-600">
                            {nextStatusObj.description}
                          </p>
                        )}
                        <p className="text-xs text-blue-500 mt-2">
                          We'll automatically update you when your order status changes.
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div>
                      <p className="text-blue-700 font-medium">Your order has been completed!</p>
                      <p className="text-sm text-blue-600">
                        Thank you for choosing our service. We hope you enjoyed your shopping experience.
                      </p>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Contact Information */}
        <div className="bg-gradient-to-r from-blue-50 to-yellow-50 rounded-2xl p-6 border border-blue-200">
          <div className="text-center">
            <h4 className="text-lg font-bold text-blue-800 mb-3">Need Help?</h4>
            <p className="text-blue-700 mb-4">
              If you have any questions about your order or need assistance, we're here to help!
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              <a 
                href="tel:+1234567890" 
                className="flex items-center bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl transition-all duration-200 transform hover:scale-105 font-medium shadow-lg"
              >
                <FaPhone className="mr-2" />
                Call Support
              </a>
              <a 
                href="mailto:support@quickcrate.com" 
                className="flex items-center bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-xl transition-all duration-200 transform hover:scale-105 font-medium shadow-lg"
              >
                <FaUser className="mr-2" />
                Email Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;